import { useNavigate } from 'react-router-dom';
import { AlertCircle, ShieldCheck, Wallet } from 'lucide-react';
import ConnectWalletButton from './wallet/ConnectWalletButton';
import GetDidCta from './GetDidCta';
import {
  canUseApplicationForm,
  needsDidOnboarding,
} from '../web3/didEligibility';
import { useWeb3Auth } from '../web3/Web3AuthContext';

const AuthForm = () => {
  const navigate = useNavigate();
  const auth = useWeb3Auth();

  const adminPending =
    auth.isAdmin || auth.eligibility?.reason === 'admin_bypass';
  const canContinueToApply = canUseApplicationForm(auth.eligibility);
  const goNext = () =>
    navigate(adminPending ? '/admin/dashboard' : '/apply');

  return (
    <div className="mx-auto max-w-md rounded-2xl bg-white p-8 shadow-md">
      <h2 className="mb-2 text-center text-2xl font-bold">Connect wallet</h2>
      <p className="mb-6 text-center text-gray-600">
        {auth.isAdmin || adminPending
          ? 'Assigned admin wallets click Connect Wallet, then Sign in as Admin. No APTMDAO NFT or Global DID is required for Admin.'
          : 'Click Connect Wallet, then sign a message to prove you control it. Your details are only recorded when you submit an application.'}
      </p>

      <ol className="mb-6 space-y-2 text-sm text-slate-600">
        <li className="flex items-start gap-2">
          <Wallet size={16} className="mt-0.5 text-primary" />
          1. Connect wallet and switch to Apertum
        </li>
        <li className="flex items-start gap-2">
          <ShieldCheck size={16} className="mt-0.5 text-primary" />
          {auth.isAdmin || adminPending
            ? '2. Assigned admin — we ask you to sign once to open Admin'
            : '2. Sign a message in your wallet to prove you control this account'}
        </li>
        <li className="flex items-start gap-2">
          <ShieldCheck size={16} className="mt-0.5 text-primary" />
          {auth.isAdmin || adminPending
            ? '3. Admin tools do not require a Global DID'
            : '3. Complete Global DID KYC, then apply. Submitting records your wallet and DID on-chain'}
        </li>
      </ol>

      <div className="flex justify-center">
        <ConnectWalletButton
          onSignedIn={() => {
            if (adminPending || canContinueToApply) goNext();
          }}
        />
      </div>

      {auth.loginStep === 'signed_in' && (auth.isAdmin || canContinueToApply) && (
        <button
          type="button"
          onClick={goNext}
          className="mt-4 w-full rounded-xl bg-primary py-2 text-sm font-semibold text-white hover:bg-primary-600"
        >
          {auth.isAdmin ? 'Continue to admin dashboard' : 'Continue to application'}
        </button>
      )}

      {auth.error && (
        <div className="mt-4 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <p>{auth.error}</p>
        </div>
      )}

      {auth.loginStep === 'signed_in' && auth.isAdmin && (
        <p className="mt-4 text-center text-sm text-green-700">Signed in as Admin</p>
      )}
      {auth.loginStep === 'signed_in' && auth.address && !auth.isAdmin && (
        <div className="mt-4 space-y-3">
          <p className="text-center text-sm text-green-700">
            Wallet signed. Nothing is recorded until you apply.
          </p>
          {auth.eligibility &&
            !auth.eligibility.eligible &&
            needsDidOnboarding(auth.eligibility.reason) && (
              <div className="flex justify-center">
                <GetDidCta
                  centered
                  reason={auth.eligibility.reason}
                  message={auth.eligibility.message}
                />
              </div>
            )}
        </div>
      )}
      {auth.loginStep === 'needs_signature' && auth.address && (
        <p className="mt-4 text-center text-sm text-slate-600">
          Approve the signature request in your wallet to finish connecting.
        </p>
      )}
    </div>
  );
};

export default AuthForm;
